import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo } from 'react';

import type { VoiceRoomState } from '#modules/finder/features/posts/components/Post/VoiceRoomBlock';
import { useCallStore } from '#features/calls/model/callStore';
import { getVoiceRoomQueryOptions } from '#features/voice/model/voice';
import { useVoiceStore } from '#features/voice/model/voiceStore';

/**
 * Код отказа с сервера → состояние блока. Все прочие коды показываем как «перегружен»: это
 * единственный отказ, который предлагает запасной путь, и для неизвестной поломки он честнее
 * молчания.
 */
const STATE_BY_ERROR: Record<string, VoiceRoomState> = {
  'voice.room.banned': 'kicked',
  'voice.user.banned': 'restricted',
  'voice.room.full': 'full',
  'voice.not.member': 'restricted',
  'voice.mode.disabled': 'off',
  /*
    «Сессия не идёт» - это конец, а не ожидание старта. Раньше код вёл в `scheduled`, и человек,
    которому хост завершил сессию досрочно, читал «Голос откроется за 10 минут до старта», хотя
    старт давно прошёл. Голос сессии открыт с её создания, так что «ещё не открылся» здесь
    невозможно по построению: сервер отвечает так только когда сессии уже нет.
  */
  'voice.session.not.live': 'finished',
  // Единственный код не с сервера: его ставит сам клиент, когда медиасервер выбил нас в пользу
  // соседней вкладки.
  'voice.other.tab': 'otherTab',
  'voice.call.active': 'callBusy',
  // Тоже клиентский: голос отвалился сам — сеть пропала или медиасервер разорвал соединение.
  'voice.connection.lost': 'connectionLost',
  // `voice.connect.failed` (тоже клиентский, ставится при отказе room.connect) намеренно НЕ
  // перечислен: он должен попадать в дефолтный `overloaded` — единственное состояние, которое
  // предлагает запасной путь. Человеку, до которого не доехал медиасервер, нужен именно он, а не
  // ещё одна формулировка «что-то пошло не так».
};

type UseVoiceRoomStateArgs = {
  sessionId: string;
  /** PLATFORM | DISCORD | IN_GAME | NONE. Пусто у старых сессий — считаем платформенным. */
  voiceMode?: string | null;
  /** Сколько слотов в сессии — вместимость комнаты равна им. */
  teamLimit?: number;
  /**
   * Страховочный опрос снимка комнаты, пока я не в голосе. Включают его поверхности комнаты
   * сессии — там «сколько нас в голосе» и есть содержание. Карточки в ленте живут кадром по
   * WebSocket: десять живых заявок на экране — это десять лишних запросов каждые двадцать секунд
   * ради числа, на которое в ленте никто не смотрит.
   */
  poll?: boolean;
};

export type VoiceRoomStateSnapshot = {
  state: VoiceRoomState;
  isPlatform: boolean;
  /** Я в голосе именно этой сессии. */
  inThisRoom: boolean;
  /** undefined: ответ ещё не пришёл; CLOSED также означает отсутствие комнаты. */
  roomStatus: string | undefined;
  connectedCount: number;
  capacity: number;
  speakingName: string | null;
  /** Личный звонок держит микрофон. */
  onCall: boolean;
  /** Код последнего отказа по этой сессии. */
  lastError: string | null;
};

/**
 * Одно состояние комнаты на все её поверхности: карточку заявки, полосу над перепиской, пилюлю и
 * ярус голоса. Раньше пятнадцать веток жили внутри `SessionVoice`, и каждая новая поверхность
 * либо тащила его целиком, либо заводила свой, чуть иначе упорядоченный расчёт — а порядок здесь
 * и есть смысл.
 *
 * Порядок проверок неслучаен: сначала то, что человек изменить не может (режим, ограничение
 * аккаунта), затем моя собственная фаза, потом занятость микрофона личным звонком — и только в
 * конце состояние комнаты.
 */
export const useVoiceRoomState = ({
  sessionId,
  voiceMode,
  teamLimit = 0,
  poll = false,
}: UseVoiceRoomStateArgs): VoiceRoomStateSnapshot => {
  const mode = (voiceMode || 'PLATFORM').toUpperCase();
  const isPlatform = mode === 'PLATFORM';

  const phase = useVoiceStore((s) => s.phase);
  const activeRoom = useVoiceStore((s) => s.room);
  const members = useVoiceStore((s) => s.members);
  /** Я был в голосе этой сессии и не выходил — см. `voiceStore.lastVoiceSessionId`. */
  const wasInThisRoom = useVoiceStore((s) => s.lastVoiceSessionId === sessionId);
  const forgetVoiceSession = useVoiceStore((s) => s.forgetVoiceSession);
  // Отказ показываем только свой и только по этой сессии: код в сторе один на всё приложение.
  const lastError = useVoiceStore((s) => (s.lastErrorSessionId === sessionId ? s.lastError : null));
  // Принятый звонок держит микрофон — в комнату сервер не пустит, и говорить об этом надо заранее.
  const onCall = useCallStore((s) => s.phase === 'active');

  /** Я в голосе именно этой сессии — чужой разговор состояние этой карточки не меняет. */
  const inThisRoom = isPlatform && activeRoom?.sessionId === sessionId && phase !== 'idle';

  const { data: snapshot } = useQuery({
    // Изнутри комнаты опрашивать нечего: живой состав LiveKit опережает снимок на несколько
    // секунд, и второй источник той же правды тут только спорил бы с первым.
    ...getVoiceRoomQueryOptions(sessionId, { poll: isPlatform && poll && !inThisRoom }),
    enabled: isPlatform,
  });
  const room = isPlatform ? snapshot : undefined;

  /**
   * Меня выбило из этой комнаты, и вернуться есть куда.
   *
   * `OPEN` здесь обязателен: у закончившейся сессии комнаты уже нет, и «вернуться» звало бы в
   * никуда. Он же отвечает за случай «в голосе я был один»: сервер закрывает опустевшую комнату,
   * и после перезагрузки честный ответ — обычное «Войти», а не возврат в разговор, которого нет.
   */
  const canReturn = wasInThisRoom && !inThisRoom && room?.status === 'OPEN';

  /*
    Отметка о голосе умирает вместе с сессией.

    Про закончившуюся сессию `canReturn` и так молчит, но оставленная запись пережила бы и
    следующий вход в приложение: комната у неё одна, а `localStorage` вечен. Чистим ровно там,
    где стало точно известно, что возвращаться некуда, — снимок приехал (`undefined` — это «ещё
    не спросили») и комнаты в нём либо нет, либо она закрыта.
  */
  const staleMark = wasInThisRoom && !inThisRoom && room !== undefined && room?.status !== 'OPEN';
  useEffect(() => {
    if (staleMark) {
      forgetVoiceSession();
    }
  }, [staleMark, forgetVoiceSession]);

  const state: VoiceRoomState = useMemo(() => {
    if (mode === 'NONE') {
      return 'off';
    }
    if (mode === 'DISCORD') {
      return 'discord';
    }
    if (mode === 'IN_GAME') {
      return 'inGame';
    }

    if (inThisRoom) {
      if (phase === 'connecting') {
        return 'connecting';
      }
      if (phase === 'reconnecting') {
        return 'reconnecting';
      }
      return 'joined';
    }

    // Сервер откажет тем же кодом, но спрашивать его незачем: состояние звонка мы знаем и так,
    // а «нажал — подождал — отказали» хуже, чем «сказали сразу».
    if (onCall) {
      return 'callBusy';
    }

    // Код живёт в сторе до следующей попытки входа.
    if (lastError) {
      return STATE_BY_ERROR[lastError] ?? 'overloaded';
    }

    /*
      Часового замка больше нет: голос сессии открыт с её создания, и в него заходят, чтобы
      дождаться остальных. Раньше здесь стояло «до старта меньше десяти минут – иначе
      `scheduled`», и человек, пришедший заранее, мог только писать. Само состояние `scheduled`
      осталось в наборе для полноты, но сюда больше не приходит ниоткуда: отказ сервера
      «сессия не идёт» означает конец сессии и ведёт в `finished`.
    */

    const connected = room?.connectedCount ?? 0;
    if (teamLimit > 0 && connected >= teamLimit) {
      return 'full';
    }

    /*
      Голос после перезагрузки возвращается сам (`useVoiceAutoRejoin`, 02.10.2026), но пока
      возврат идёт – или сервер его пережидает, – карточка обязана говорить, что связи нет.

      Пока этой ветки не было, состояние `connectionLost` доставалось
      только тому, кого выбило на живой вкладке: код отказа ставит `RoomEvent.Disconnected`, и
      живёт он в памяти вкладки, которой после F5 уже нет. Человек видел «1/2 · Войти» и ярус
      «Идёт разговор» — комната говорила, что всё в порядке, а слышно его не было.

      Ветка та же самая, вход в неё — второй: не «мне только что отказали», а «я отсюда не
      выходил». Стоит после отказов и после `full` намеренно: свежий отказ сервера знает про
      комнату больше, чем наша память о ней, а звать в полную комнату — обещать то, чего сервер
      не даст.
    */
    if (canReturn) {
      return 'connectionLost';
    }
    return connected > 0 ? 'occupied' : 'empty';
  }, [mode, inThisRoom, phase, onCall, lastError, room?.connectedCount, teamLimit, canReturn]);

  // Пока я в комнате, счётчик берётся из живого состава LiveKit: он опережает серверный на
  // несколько секунд, и «3 из 5» не должно спорить с тем, кого видно в списке.
  const connectedCount = inThisRoom
    ? members.filter((member) => member.connected).length
    : (room?.connectedCount ?? 0);

  const speakingName = inThisRoom
    ? (members.find((member) => member.speaking)?.displayName ?? null)
    : null;

  return {
    state,
    isPlatform,
    inThisRoom,
    roomStatus: room === undefined ? undefined : (room?.status ?? 'CLOSED'),
    connectedCount,
    capacity: teamLimit || (room?.maxParticipants ?? 0),
    speakingName,
    onCall,
    lastError,
  };
};
